import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import ErrorScreen from "../../../components/ErrorScreen";
import {
  AllocationItem,
  CreateCartAllocationRequest,
  useCreateCartAllocationMutation,
  useDeleteCartAllocationPlanMutation,
  useGetAllCartAllocationPlansQuery,
  useLazyGetCartAllocationPlanByIdQuery,
  useLazyGetCartAllocationsQuery,
  useUpdateActiveCartAllocationPlanMutation,
  useUpdateCartAllocationPlanMutation,
} from "../../../redux/services/DistributionService";
import AllocationTable from "./AllocationTable";
import Header from "./Header";
import "./index.css";
import PlanCards from "./PlanCards";
import SaveVersionModal from "./SaveVersionModal";
import VersionManagement from "./VersionManagement";
import {
  setAllocations,
  setVersions,
  setCurrentVersionId,
  setSelectedPlanId,
  setSelectedDate,
  setHasUnsavedChanges,
  setOriginalAllocations,
  setLoading,
  setError,
  updateAllocation,
  resetAllToSuggested,
  resetToSuggested,
  saveVersion,
} from "../../../redux/cartAllocationSlice";
import {
  CartAllocation,
  CartAllocations,
  PlanVersion,
} from "../../../redux/distributionSlice";
import BreadCrumbs from "../../../components/BreadCrumbs";
import { handleApiError } from "../../../utility/errorUtility";
import AllocationTableLoading from "../../../components/AllocationTableLoading";

// Type guard to check if value is CartAllocation
const isCartAllocation = (value: any): value is CartAllocation => {
  return (
    value &&
    typeof value === "object" &&
    "cartName" in value &&
    "allocations" in value
  );
};

// Type guard to check if error is a FetchBaseQueryError with status property
const isFetchBaseQueryError = (
  error: any
): error is { status: number; data: unknown } => {
  return error && typeof error === "object" && "status" in error;
};

// Type guard to check if error is a SerializedError
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const isSerializedError = (
  error: any
): error is { message?: string; code?: string } => {
  return (
    error &&
    typeof error === "object" &&
    ("message" in error || "code" in error)
  );
};

const CartAllocationPlanning: React.FC = () => {
  const dispatch = useDispatch();
  const {
    allocations,
    versions,
    currentVersionId,
    selectedPlanId,
    selectedDate,
    hasUnsavedChanges,
    originalAllocations,
    isLoading: storeLoading,
  } = useSelector((state: any) => state.cartAllocation);

  const [showVersionModal, setShowVersionModal] = useState<boolean>(false);
  const [newVersionName, setNewVersionName] = useState<string>("");
  const [apiErrorMessage, setApiErrorMessage] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [saveMode, setSaveMode] = useState<"new" | "update">("update");
  const [versionError, setVersionError] = useState<string | null>(null);
  const [isPlanSwitching, setIsPlanSwitching] = useState(false); // Track plan switching

  const RestID = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails.id
  );

  const restaurantId = RestID;
  const inventoryId = selectedDate.replace(/-/g, "");

  // API hooks with error handling for all
  const [
    triggerGetCartAllocations,
    { isLoading: apiLoading, isError: apiError, error: cartAllocationsError },
  ] = useLazyGetCartAllocationsQuery();

  const [
    triggerGetCartAllocationPlanById,
    {
      data: selectedPlanData,
      isLoading: selectedPlanLoading,
      isError: selectedPlanError,
      error: selectedPlanErrorData,
    },
  ] = useLazyGetCartAllocationPlanByIdQuery();

  const {
    data: allPlans = [],
    refetch: refetchAllPlans,
    isError: allPlansError,
    error: allPlansErrorData,
  } = useGetAllCartAllocationPlansQuery(restaurantId, {
    skip: !restaurantId,
  });

  const [createCartAllocation, { isLoading: isSaving }] =
    useCreateCartAllocationMutation();
  const [updateActiveCartAllocationPlan, { isLoading: isUpdatingActivePlan }] =
    useUpdateActiveCartAllocationPlanMutation();
  const [updateCartAllocationPlan, { isLoading: isUpdating }] =
    useUpdateCartAllocationPlanMutation();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [deleteCartAllocationPlan, { isLoading: isDeleting }] =
    useDeleteCartAllocationPlanMutation();

  const [prevRestaurantId, setPrevRestaurantId] = useState<string | null>(null);

  useEffect(() => {
    if (prevRestaurantId && prevRestaurantId !== restaurantId) {
      dispatch(setSelectedPlanId(null));
      dispatch(setCurrentVersionId(null));
    }
    setPrevRestaurantId(restaurantId);
  }, [restaurantId, prevRestaurantId, dispatch]);

  const is404Error = (error: any): boolean => {
    if (isFetchBaseQueryError(error)) {
      return error.status === 404;
    }
    return false;
  };

  useEffect(() => {
    let errorMessage: string | null = null;

    if (apiError && cartAllocationsError) {
      errorMessage = handleApiError(cartAllocationsError, {
        defaultMessage: "Failed to load allocation data",
      });
    } else if (allPlansError && allPlansErrorData) {
      if (!is404Error(allPlansErrorData)) {
        errorMessage = handleApiError(allPlansErrorData, {
          defaultMessage: "Failed to load allocation plans",
        });
      }
    } else if (selectedPlanError && selectedPlanErrorData) {
      if (selectedPlanId && prevRestaurantId === restaurantId) {
        errorMessage = handleApiError(selectedPlanErrorData, {
          defaultMessage: "Failed to load selected plan",
        });
      } else if (
        is404Error(selectedPlanErrorData) &&
        prevRestaurantId !== restaurantId
      ) {
        dispatch(setSelectedPlanId(null));
        dispatch(setCurrentVersionId(null));
      }
    }

    if (errorMessage) {
      setApiErrorMessage(errorMessage);
      dispatch(setError(errorMessage));
    } else if (apiErrorMessage) {
      setApiErrorMessage(null);
      dispatch(setError(null));
    }
  }, [
    apiError,
    cartAllocationsError,
    allPlansError,
    allPlansErrorData,
    selectedPlanError,
    selectedPlanErrorData,
    selectedPlanId,
    restaurantId,
    prevRestaurantId,
    apiErrorMessage,
    dispatch,
  ]);

  // Fetch allocations from API
  useEffect(() => {
    const fetchData = async () => {
      if (!restaurantId) return;

      dispatch(setLoading(true));
      setApiErrorMessage(null);

      try {
        if (selectedPlanId) {
          if (prevRestaurantId === restaurantId) {
            const result = await triggerGetCartAllocationPlanById({
              restaurantId,
              planId: selectedPlanId,
            });

            if (result.data) {
              const planData = result.data;
              const transformedAllocations: CartAllocations = {};

              planData.carts.forEach((cart) => {
                transformedAllocations[cart.cartId] = {
                  cartName: cart.cartName,
                  abbreviation: cart.abbreviation || "",
                  allocations: cart.allocations.map((item) => ({
                    productItemId: item.productItemId,
                    abbreviation: item.abbreviation || "",
                    displayName: item.displayName,
                    portionSize: item.portionSize,
                    directIssues: item.directIssues,
                  })),
                };
              });

              dispatch(setAllocations(transformedAllocations));
              dispatch(setOriginalAllocations(transformedAllocations));
              dispatch(setHasUnsavedChanges(false));
            } else if (result.error) {
              if (prevRestaurantId === restaurantId) {
                const errorMessage = handleApiError(result.error, {
                  defaultMessage: "Failed to load allocation plan",
                });
                setApiErrorMessage(errorMessage);
                dispatch(setError(errorMessage));
              }
            }
          }
        } else {
          const result = await triggerGetCartAllocations({
            restaurantId,
            inventoryId,
          });

          if (result.data) {
            dispatch(setAllocations(result.data));

            const dateKey = selectedDate;
            const dateVersions = versions[selectedDate] || {};
            if (!dateVersions || Object.keys(dateVersions).length === 0) {
              const defaultVersionId = `v1_${Date.now()}`;
              const newVersions = {
                ...versions,
                [dateKey]: {
                  [defaultVersionId]: {
                    id: defaultVersionId,
                    name: "Initial Plan",
                    data: result.data,
                    createdAt: new Date().toISOString(),
                    isActive: true,
                    isDefault: true,
                    isInitialPlan: true,
                  },
                },
              };
              dispatch(setVersions(newVersions));
              dispatch(setCurrentVersionId(defaultVersionId));
            }

            dispatch(setOriginalAllocations(result.data));
          } else if (result.error) {
            const errorMessage = handleApiError(result.error, {
              defaultMessage: "Failed to load cart allocations",
            });
            setApiErrorMessage(errorMessage);
            dispatch(setError(errorMessage));
          }
        }
      } catch (error) {
        const errorMessage = handleApiError(error, {
          defaultMessage: "An unexpected error occurred while loading data",
        });
        setApiErrorMessage(errorMessage);
        dispatch(setError(errorMessage));
        toast.error(errorMessage);
      } finally {
        dispatch(setLoading(false));
        setIsPlanSwitching(false);
      }
    };

    fetchData();
  }, [
    selectedDate,
    restaurantId,
    selectedPlanId,
    inventoryId,
    dispatch,
    triggerGetCartAllocations,
    triggerGetCartAllocationPlanById,
    versions,
    prevRestaurantId,
  ]);

  const getProductCombinations = (
    allocations: CartAllocations
  ): AllocationItem[] => {
    const uniqueProducts = new Map();

    Object.values(allocations).forEach((cartAllocation) => {
      cartAllocation.allocations.forEach((item) => {
        const key = `${item.productItemId}-${item.portionSize}`;
        if (!uniqueProducts.has(key)) {
          uniqueProducts.set(key, { ...item });
        }
      });
    });

    return Array.from(uniqueProducts.values());
  };

  const handleUpdateAllocation = (
    cartId: string,
    productItemId: string,
    portionSize: string,
    value: string
  ): void => {
    const numericValue = Math.max(0, parseInt(value) || 0);
    dispatch(
      updateAllocation({
        cartId,
        productItemId,
        portionSize,
        value: numericValue,
      })
    );
  };

  const handleResetAllToSuggested = (): void => {
    dispatch(resetAllToSuggested());
  };

  const getCartTotal = (cartId: string): number => {
    const cartAllocation = allocations[cartId];
    if (!cartAllocation) return 0;

    return cartAllocation.allocations.reduce(
      (sum: number, item: AllocationItem) => sum + item.directIssues,
      0
    );
  };

  const getProductTotal = (
    productItemId: string,
    portionSize: string
  ): number => {
    return Object.values(allocations as CartAllocation[]).reduce(
      (sum: number, cartAllocation: CartAllocation) => {
        const item = cartAllocation.allocations.find(
          (a: AllocationItem) =>
            a.productItemId === productItemId && a.portionSize === portionSize
        );
        return sum + (item?.directIssues || 0);
      },
      0
    );
  };

  const handleSave = async (
    mode: "update" | "new" = "update"
  ): Promise<void> => {
    const currentVersion = getCurrentVersion();

    if (currentVersion?.isInitialPlan && mode === "update") {
      mode = "new";
    }

    if (mode === "update" && selectedPlanId) {
      try {
        const updateRequest = {
          restaurantId: restaurantId,
          planId: selectedPlanId,
          allocationPlanVersion: selectedPlanData?.allocationPlanVersion || "",
          carts: Object.entries(allocations).map(([cartId, cartAllocation]) => {
            if (!isCartAllocation(cartAllocation)) {
              throw new Error(`Invalid cart allocation for cartId: ${cartId}`);
            }
            return {
              cartId: cartId,
              cartName: cartAllocation.cartName,
              allocations: cartAllocation.allocations.map(
                (item: AllocationItem) => ({
                  productItemId: item.productItemId,
                  portionSize: item.portionSize as
                    | "SMALL"
                    | "REGULAR"
                    | "LARGE",
                  displayName: item.displayName,
                  directIssues: item.directIssues,
                })
              ),
            };
          }),
        };

        await updateCartAllocationPlan(updateRequest).unwrap();
        dispatch(setHasUnsavedChanges(false));
        dispatch(setOriginalAllocations({ ...allocations }));
        toast.success("Plan updated successfully!");
      } catch (error: any) {
        const errorMessage = handleApiError(error, {
          defaultMessage: "Failed to update plan. Please try again.",
        });
        toast.error(errorMessage);
      }
    } else if (mode === "update") {
      const dateKey = selectedDate;
      const currentVersion = versions[dateKey][currentVersionId as string];

      dispatch(
        saveVersion({
          versionId: currentVersionId as string,
          versionData: {
            ...currentVersion,
            data: { ...allocations },
            updatedAt: new Date().toISOString(),
          },
        })
      );

      toast.success("Changes saved to current version!");
    } else {
      setSaveMode(mode);
      setShowVersionModal(true);
    }
  };

  const saveNewVersion = async (makeActive: boolean = false): Promise<void> => {
    if (!newVersionName.trim()) {
      setVersionError("Please enter a version name");
      return;
    }

    try {
      const allocationRequest: CreateCartAllocationRequest = {
        restaurantId: restaurantId,
        allocationPlanVersion: newVersionName.trim(),
        carts: Object.entries(allocations).map(([cartId, cartAllocation]) => {
          if (!isCartAllocation(cartAllocation)) {
            throw new Error(`Invalid cart allocation for cartId: ${cartId}`);
          }
          return {
            cartId: cartId,
            cartName: cartAllocation.cartName,
            allocations: cartAllocation.allocations.map(
              (item: AllocationItem) => ({
                productItemId: item.productItemId,
                portionSize: item.portionSize as "SMALL" | "REGULAR" | "LARGE",
                displayName: item.displayName,
                directIssues: item.directIssues,
              })
            ),
          };
        }),
      };

      await createCartAllocation(allocationRequest).unwrap();

      const message = makeActive
        ? `Version "${newVersionName}" saved and set as active!`
        : `Version "${newVersionName}" saved successfully!`;
      toast.success(message);

      dispatch(setHasUnsavedChanges(false));
      dispatch(setOriginalAllocations({ ...allocations }));
      setShowVersionModal(false);
      setNewVersionName("");
      setVersionError(null);

      refetchAllPlans();
    } catch (error: any) {
      const errorMessage = handleApiError(error, {
        defaultMessage: "Failed to save allocation. Please try again.",
      });
      setVersionError(errorMessage);
    }
  };

  const loadVersion = (versionId: string): void => {
    if (hasUnsavedChanges) {
      Swal.fire({
        title: "Unsaved Changes",
        text: "You have unsaved changes. Loading another version will lose these changes. Continue?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes",
        cancelButtonText: "No",
      }).then((result: any) => {
        if (result.isConfirmed) {
          const dateVersions = versions[selectedDate] || {};
          const version = dateVersions[versionId];
          if (version) {
            dispatch(setAllocations(version.data));
            dispatch(setOriginalAllocations(version.data));
            dispatch(setCurrentVersionId(versionId));
            dispatch(setHasUnsavedChanges(false));
          }
        }
      });
    } else {
      const dateVersions = versions[selectedDate] || {};
      const version = dateVersions[versionId];
      if (version) {
        dispatch(setAllocations(version.data));
        dispatch(setOriginalAllocations(version.data));
        dispatch(setCurrentVersionId(versionId));
        dispatch(setHasUnsavedChanges(false));
      }
    }
  };

  const handleDeletePlan = async (planId: string) => {
    try {
      await deleteCartAllocationPlan({
        planId,
        restaurantId: RestID,
      }).unwrap();
      refetchAllPlans();
      toast.success("Plan deleted successfully!");
    } catch (error) {
      const errorMessage = handleApiError(error, {
        defaultMessage: "Failed to delete the plan. Please try again.",
      });
      Swal.fire({
        title: "Error!",
        text: errorMessage,
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const handlePlanSelect = (planId: string) => {
    setIsPlanSwitching(true); // Set plan switching state
    if (hasUnsavedChanges) {
      Swal.fire({
        title: "Unsaved Changes",
        text: "You have unsaved changes. Loading another plan will lose these changes. Continue?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes",
        cancelButtonText: "No",
      }).then((result) => {
        if (result.isConfirmed) {
          dispatch(setSelectedPlanId(planId));
          dispatch(setCurrentVersionId(null));
        } else {
          setIsPlanSwitching(false); // Reset if user cancels
        }
      });
    } else {
      dispatch(setSelectedPlanId(planId));
      dispatch(setCurrentVersionId(null));
    }
  };

  const handleSetActivePlan = async (
    planId: string,
    isCurrentlyActive: boolean
  ) => {
    if (isCurrentlyActive) return;

    try {
      await updateActiveCartAllocationPlan({
        restaurantId,
        planId,
        isActive: true,
      }).unwrap();
      refetchAllPlans();
      toast.success("Plan set as active successfully!");
    } catch (error) {
      const errorMessage = handleApiError(error, {
        defaultMessage: "Failed to set plan as active. Please try again.",
      });
      toast.error(errorMessage);
    }
  };

  const getCurrentVersion = (): PlanVersion | undefined => {
    const dateVersions = versions[selectedDate] || {};
    return dateVersions[currentVersionId as string];
  };

  const handleResetToSuggested = (
    cartId: string,
    productItemId: string,
    portionSize: string
  ): void => {
    dispatch(
      resetToSuggested({
        cartId,
        productItemId,
        portionSize,
      })
    );
  };

  const handleDateChange = (date: string) => {
    dispatch(setSelectedDate(date));
    dispatch(setSelectedPlanId(null));
    dispatch(setCurrentVersionId(null));
  };

  const numCarts = Object.keys(allocations).length || 3;
  const numProducts = getProductCombinations(allocations).length || 5;

  const showErrorScreen = apiError && cartAllocationsError;

  return (
    <>
      <BreadCrumbs hasSubMenus={true} />
      <div className="cart-allocation-container">
        <div className="cart-allocation-content">
          <Header
            selectedDate={selectedDate}
            setSelectedDate={handleDateChange}
            resetAllToSuggested={handleResetAllToSuggested}
            handleSave={handleSave}
            hasUnsavedChanges={hasUnsavedChanges}
            selectedPlanId={selectedPlanId}
            selectedPlanData={selectedPlanData}
            isSaving={isSaving}
            isUpdating={isUpdating}
          />

          {showErrorScreen ? (
            <ErrorScreen
              onRetry={() => window.location.reload()}
              errorMessage={apiErrorMessage || "Failed to load allocation data"}
            />
          ) : (
            <>
              <PlanCards
                allPlans={allPlans}
                selectedPlanId={selectedPlanId}
                handlePlanSelect={handlePlanSelect}
                hasUnsavedChanges={hasUnsavedChanges}
                setSelectedPlanId={(id) => dispatch(setSelectedPlanId(id))}
                onSetActivePlan={handleSetActivePlan}
                isUpdatingActivePlan={isUpdatingActivePlan}
                onDeletePlan={handleDeletePlan}
                refetchAllPlans={refetchAllPlans}
              />

              {!selectedPlanId && (
                <VersionManagement
                  versions={versions}
                  selectedDate={selectedDate}
                  currentVersionId={currentVersionId}
                  loadVersion={loadVersion}
                  hasUnsavedChanges={hasUnsavedChanges}
                  onSetActivePlan={handleSetActivePlan}
                  isUpdatingActivePlan={isUpdatingActivePlan}
                />
              )}

              <SaveVersionModal
                showVersionModal={showVersionModal}
                setShowVersionModal={setShowVersionModal}
                newVersionName={newVersionName}
                setNewVersionName={setNewVersionName}
                saveNewVersion={saveNewVersion}
                isSaving={isSaving}
                allPlans={allPlans}
                setVersionError={setVersionError}
                versionError={versionError}
              />

              {storeLoading ||
              apiLoading ||
              selectedPlanLoading ||
              isPlanSwitching ? (
                <AllocationTableLoading
                  numCarts={numCarts}
                  numProducts={numProducts}
                />
              ) : (
                <AllocationTable
                  allocations={allocations}
                  updateAllocation={handleUpdateAllocation}
                  getCartTotal={getCartTotal}
                  getProductTotal={getProductTotal}
                  getProductCombinations={getProductCombinations}
                  originalAllocations={originalAllocations}
                  resetToSuggested={handleResetToSuggested}
                  isLoading={storeLoading || apiLoading || selectedPlanLoading}
                />
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default CartAllocationPlanning;
